/**
 * Chat turn classification and frame routing, shared by both clients.
 *
 * It was the rules of a trip's SHARED agent chat (COLLABORATION_PLAN CD-4) —
 * the owner and one collaborator talking to the agent in one conversation. Beta
 * withdrew the second writer, so `senderLabel` ("You" / "Agent" / the
 * collaborator's name) and `confirmedByNote` ("Confirmed by Ana") are gone with
 * the two voices and the race they described.
 *
 * What remains is still shared and still worth running: which surface a socket
 * frame belongs on, and whether a stored turn is the reader's own.
 *
 * Deliberately import-free, so the logic is plain enough to exercise directly —
 * mobile has no test runner yet (M1.3), and its copy is pinned byte-for-byte to
 * this file.
 */

/** Who a turn came from, from the viewer's point of view. */
export type TurnFrom = 'ai' | 'me' | 'them';

/**
 * Classify a stored or live turn. A user turn is "me" only when it is provably
 * mine: every turn is stored with its sender's id, and a user turn from anyone
 * else is not yours — calling it "You" would put another person's words in your
 * mouth. With one writer per chat "them" should never occur, and this is the
 * check that makes that a fact rather than an assumption.
 */
export function turnFrom(actor: string | undefined, senderId: string | undefined, me: string | undefined): TurnFrom {
  if (actor === 'assistant') return 'ai';
  if (me && senderId && senderId !== me) return 'them';
  return 'me';
}

/**
 * Does a socket frame belong on this chat surface?
 *
 * A user's socket carries EVERY chat they are in: a message the collaborator sends
 * in the trip chat also reaches this person's Plan-tab socket, and vice versa. A
 * frame for another conversation must be ignored, or one chat's replies would
 * spill into the other. Frames with no conversation id (connection notices,
 * send errors) belong to whoever is listening.
 */
export function frameBelongs(frameConversationId: string | undefined, chatConversationId: string | undefined): boolean {
  if (!frameConversationId || !chatConversationId) return true;
  return frameConversationId === chatConversationId;
}

/** A person's display name: "First Last", else their nickname, else nothing. */
export function displayName(profile: {
  firstName?: string | null;
  lastName?: string | null;
  nickname?: string | null;
} | null | undefined): string | undefined {
  if (!profile) return undefined;
  const full = [profile.firstName, profile.lastName].map((part) => (part || '').trim()).filter(Boolean).join(' ');
  return full || (profile.nickname || '').trim() || undefined;
}
